import { HistoryService } from '@/api'
import { Button } from '@/cn/ui/button'
import { ITx, IUser, OrderStatus } from '@/interface'
import { ACTIVE_CURRENCY, prettifyAddress, queryClient, secondsToHours } from '@/utils'
import { useQuery } from '@tanstack/react-query'
import { Check, ExternalLink, Loader } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTokens } from '@/hooks/use_tokens'
import { CHAINS } from '@/constants/chains'

const handleDownload = async (ref: any) => {
  const { default: html2canvas } = await import('html2canvas')
  const canvas = await html2canvas(ref.current)
  const imgData = canvas.toDataURL('image/png')
  var encodedUri = encodeURI(imgData)
  var link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', 'wowza.png')
  document.body.appendChild(link) // Required for FF
  link.click()
}

const PayemntStatus = ({ order }: { order: ITx }) => {
  const [chainId, setChainId] = useState<any>()
  const [chains, setChains] = useState<any>([])
  const [remainingTime, setRemainingTime] = useState(1000)
  const ShareDownload = useRef(null)
  const tokens = useTokens()

  const transaction = useQuery(['transactions', order._id], () => HistoryService.GetById(order._id), {
    enabled: !!order._id,
    refetchInterval: 1000,
  })
  const user = queryClient.getQueryData(['user']) as IUser
  const navigation = useNavigate()
  const handleBack = () => navigation('/dashboard/')
  const { amount } = useParams()

  function capitalize(str: string) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
  useEffect(() => {
    if (!order._id) return
    setRemainingTime((Number(new Date(order.createdAt)) - Number(new Date()) + order.QR_EXPIRE_TIME) / 1000)

    let chains = order.QR_ENABLED_CHAINS.map((chainId: any) => CHAINS[chainId]).filter((chain: any) => !chain.testnet)
    if (!chains[0]) {
      alert('network not supported')
    }
    setChains(chains)
    setChainId(chains[0].id)
    const interval = setInterval(() => {
      setRemainingTime((state) => state - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [order])
  const QR = useMemo(() => {
    if (!order || tokens.isLoading || !chainId) return null
    const tokenAddress = tokens.data[chainId].tokenAddress
    const amount = order.qrId
    const QRGatewayContractAddresses = order.QRGatewayContractAddresses[chainId]
    return `ethereum:${tokenAddress}@${chainId}/transfer?uint256=${amount}&address=${QRGatewayContractAddresses}&orderId=${order._id}`
  }, [order, tokens, chainId])

  if (!order._id) {
    return (
      <div className="flex relative flex-col items-center gap-4 px-4 justify-center bg-white p-8 rounded-lg shadow-lg w-72 h-72 mt-20">
        <Loader className=" animate-spin-slow " size={24} />
      </div>
    )
  }
  if (remainingTime < 0)
    return (
      <div className="flex flex-col items-center gap-4 px-4 justify-center bg-white p-8 rounded-lg shadow-lg w-72 h-72 mt-20">
        <img src="/icons/cross_icon.png" className="w-16" />
        <h4>Transaction Timout</h4>
      </div>
    )
  switch (transaction.data?.result?.status) {
    case OrderStatus.INITIALIZED:
      return (
        <div className="w-full flex justify-center flex-col items-center" ref={ShareDownload}>
          <div className="mt-20">
            <div className="font-medium text-center text-[16px] mb-3">
              {capitalize(user.name)} <img src="/verified.png" width={'20px'} height={'20px'} alt="" />{' '}
            </div>
            <div className="flex gap-1 text-4xl font-bold ">
              <div>{ACTIVE_CURRENCY.currency}</div>
              <div>{amount}</div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 px-4 justify-center bg-white p-3 rounded-lg shadow-lg w-64 h-64 mt-5">
            <div className="flex flex-col items-center gap-2">
              <QRCodeSVG value={QR!} size={180} />
            </div>
          </div>
          <div className="flex text-bold justify-center items-center gap-4 text-center font-bold mt-4 text-[#f96900]">
            <span>Expire</span>
            <span className=" leading-3">{secondsToHours(remainingTime)}</span>
          </div>
          <div className="flex text-bold justify-center items-center gap-4 text-center font-bold mt-4 flex-wrap">
            {chains.map((chain: any) => (
              <button
                onClick={() => setChainId(chain.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full shrink-0 ${chainId === chain.id ? 'ring-2 ring-[#f96900] bg-[#f96900]/10 ' : ''}`}
              >
                <img src={`https://app.cray.pro/icons/chains/${chain.id}.svg`} className="h-6" />
                <p>{chain.name}</p>
              </button>
            ))}
          </div>
          <div className="mt-4">Scan with Camera, Cray or any wallet to Pay</div>

          <div className="flex gap-20   absolute bottom-5">
            <button
              onClick={() => {
                handleDownload(ShareDownload)
              }}
              className="py-3 text-white rounded-2xl px-8 bg-orange-500 border border-black"
            >
              Share
            </button>
            <button onClick={handleBack} className="py-3 text-black rounded-2xl px-8 bg-orange-100 border border-black">
              Cancel
            </button>
          </div>
        </div>
      )
    case OrderStatus.CANCELLED:
      return (
        <div className="flex flex-col items-center gap-4 px-4 justify-center bg-white p-8 rounded-lg shadow-lg w-72 h-72 mt-20">
          <img src="/icons/cross_icon.png" className="w-16" />
          <h4>Transaction Rejected</h4>
        </div>
      )
  }
  switch (transaction.data?.result?.status) {
    case OrderStatus.PROCESSING:
      return (
        <div className="flex flex-col items-center gap-4 px-4 justify-center bg-white p-8 rounded-lg shadow-lg w-72 h-72 mt-20">
          <Loader className=" animate-spin-slow " size={24} />

          <h4>Processing</h4>
        </div>
      )
    case OrderStatus.COMPLETED:
      return (
        <div className=" bg-white w-full h-full flex flex-col items-center justify-center gap-4">
          <div className="w-20 h-20 bg-primary flex items-center justify-center rounded-full mx-auto text-background">
            <Check size={48} />
          </div>
          <h4>Success</h4>
          <h3>
            {ACTIVE_CURRENCY.currency}
            {'  '}
            {amount}
            {'  '}
            Received
          </h3>
          <a href={`https://explorer.cray.pro/orderHash/${transaction.data?.result.orderHash}`} target="_blank" className=" flex gap-2 items-center underline">
            Order Hash: {prettifyAddress(transaction.data?.result.orderHash)} <ExternalLink size={18} />
          </a>
          <Button onClick={handleBack} variant="link">
            New Order
          </Button>
        </div>
      )
    case OrderStatus.FAILED:
      return (
        <div className="flex flex-col items-center gap-4 px-4 justify-center bg-white p-8 rounded-lg shadow-lg w-72 h-72 mt-20">
          <img src="/icons/cross_icon.png" className="w-16" />
          <h4>Transaction Failed</h4>
        </div>
      )
  }
}

export default PayemntStatus
